import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Hook to create a combined MediaStream for recording:
 * - Video: Canvas composite of interviewee camera (main) + avatar (picture-in-picture)
 * - Audio: Mixed audio (local microphone + remote avatar audio)
 *
 * This records the full interview experience with both participants.
 */
export const useCombinedStream = ({
  localStream,        // User's camera/mic stream from getUserMedia
  remoteVideoTrack,   // Avatar video track from LiveKit
  remoteAudioTrack,   // Avatar audio track from LiveKit
}) => {
  const [combinedStream, setCombinedStream] = useState(null);
  const [error, setError] = useState(null);
  const [isReady, setIsReady] = useState(false);

  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const audioContextRef = useRef(null);
  const mixedDestinationRef = useRef(null);
  const localSourceRef = useRef(null);
  const remoteSourceRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  // Canvas dimensions (1280x720 for HD recording)
  const CANVAS_WIDTH = 1280;
  const CANVAS_HEIGHT = 720;
  const PIP_WIDTH = 320;  // Picture-in-picture width
  const PIP_HEIGHT = 180; // Picture-in-picture height
  const PIP_MARGIN = 20;  // Margin from edges

  // Create combined stream when tracks are available
  useEffect(() => {
    const createCombinedStream = async () => {
      // Need remote video track to start
      if (!remoteVideoTrack) {
        console.log('useCombinedStream: Waiting for remote video track...');
        return;
      }

      try {
        console.log('useCombinedStream: Creating combined stream (interviewee main + avatar PiP)...');

        // 1. Create canvas for video compositing
        if (!canvasRef.current) {
          canvasRef.current = document.createElement('canvas');
          canvasRef.current.width = CANVAS_WIDTH;
          canvasRef.current.height = CANVAS_HEIGHT;
        }
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // 2. Create video elements for drawing
        // Remote video (avatar - main view)
        if (!remoteVideoRef.current) {
          remoteVideoRef.current = document.createElement('video');
          remoteVideoRef.current.autoplay = true;
          remoteVideoRef.current.playsInline = true;
          remoteVideoRef.current.muted = true; // Mute to prevent echo (audio handled separately)
        }
        const remoteMediaStreamTrack = remoteVideoTrack.mediaStreamTrack;
        if (remoteMediaStreamTrack) {
          remoteVideoRef.current.srcObject = new MediaStream([remoteMediaStreamTrack]);
          await remoteVideoRef.current.play().catch(e => console.warn('Remote video play error:', e));
          console.log('useCombinedStream: Remote video element ready');
        }

        // Local video (interviewee - picture-in-picture)
        if (localStream) {
          if (!localVideoRef.current) {
            localVideoRef.current = document.createElement('video');
            localVideoRef.current.autoplay = true;
            localVideoRef.current.playsInline = true;
            localVideoRef.current.muted = true;
          }
          const localVideoTrack = localStream.getVideoTracks()[0];
          if (localVideoTrack) {
            localVideoRef.current.srcObject = new MediaStream([localVideoTrack]);
            await localVideoRef.current.play().catch(e => console.warn('Local video play error:', e));
            console.log('useCombinedStream: Local video element ready');
          }
        }

        // 3. Start canvas rendering loop
        const renderFrame = () => {
          // Clear canvas
          ctx.fillStyle = '#1a1a2e';
          ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

          // Draw local video (interviewee) as main view - fill canvas (mirrored)
          if (localVideoRef.current && localVideoRef.current.readyState >= 2) {
            const video = localVideoRef.current;
            // Calculate aspect ratio fit
            const videoAspect = video.videoWidth / video.videoHeight;
            const canvasAspect = CANVAS_WIDTH / CANVAS_HEIGHT;

            let drawWidth, drawHeight, drawX, drawY;
            if (videoAspect > canvasAspect) {
              // Video is wider - fit to height
              drawHeight = CANVAS_HEIGHT;
              drawWidth = drawHeight * videoAspect;
              drawX = (CANVAS_WIDTH - drawWidth) / 2;
              drawY = 0;
            } else {
              // Video is taller - fit to width
              drawWidth = CANVAS_WIDTH;
              drawHeight = drawWidth / videoAspect;
              drawX = 0;
              drawY = (CANVAS_HEIGHT - drawHeight) / 2;
            }
            // Draw mirrored (more natural for interviewee self-view as main)
            ctx.save();
            ctx.translate(CANVAS_WIDTH, 0);
            ctx.scale(-1, 1);
            ctx.drawImage(video, CANVAS_WIDTH - drawX - drawWidth, drawY, drawWidth, drawHeight);
            ctx.restore();
          }

          // Draw remote video (avatar) as picture-in-picture in bottom-right
          if (remoteVideoRef.current && remoteVideoRef.current.readyState >= 2) {
            const video = remoteVideoRef.current;
            const pipX = CANVAS_WIDTH - PIP_WIDTH - PIP_MARGIN;
            const pipY = CANVAS_HEIGHT - PIP_HEIGHT - PIP_MARGIN;

            // Draw border/background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(pipX - 2, pipY - 2, PIP_WIDTH + 4, PIP_HEIGHT + 4);

            // Draw avatar video (not mirrored)
            ctx.drawImage(video, pipX, pipY, PIP_WIDTH, PIP_HEIGHT);
          }

          // Continue rendering
          animationFrameRef.current = requestAnimationFrame(renderFrame);
        };

        // Start rendering
        renderFrame();
        console.log('useCombinedStream: Canvas rendering started');

        // 4. Capture canvas as video stream
        const canvasStream = canvas.captureStream(30); // 30 FPS

        // 5. Mix audio tracks using Web Audio API
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const audioContext = audioContextRef.current;

        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        mixedDestinationRef.current = audioContext.createMediaStreamDestination();

        // Add local microphone audio
        if (localStream) {
          const localAudioTrack = localStream.getAudioTracks()[0];
          if (localAudioTrack) {
            const localAudioStream = new MediaStream([localAudioTrack]);
            localSourceRef.current = audioContext.createMediaStreamSource(localAudioStream);
            localSourceRef.current.connect(mixedDestinationRef.current);
            console.log('useCombinedStream: Added local audio to mix');
          }
        }

        // Add remote avatar audio
        if (remoteAudioTrack) {
          const remoteMediaStreamTrack = remoteAudioTrack.mediaStreamTrack;
          if (remoteMediaStreamTrack) {
            const remoteAudioStream = new MediaStream([remoteMediaStreamTrack]);
            remoteSourceRef.current = audioContext.createMediaStreamSource(remoteAudioStream);
            remoteSourceRef.current.connect(mixedDestinationRef.current);
            console.log('useCombinedStream: Added remote audio to mix');
          }
        }

        // 6. Combine canvas video + mixed audio into final stream
        const finalStream = new MediaStream();

        // Add canvas video track
        const canvasVideoTrack = canvasStream.getVideoTracks()[0];
        if (canvasVideoTrack) {
          finalStream.addTrack(canvasVideoTrack);
          console.log('useCombinedStream: Added canvas video track');
        }

        // Add mixed audio track
        const mixedAudioTrack = mixedDestinationRef.current.stream.getAudioTracks()[0];
        if (mixedAudioTrack) {
          finalStream.addTrack(mixedAudioTrack);
          console.log('useCombinedStream: Added mixed audio track');
        }

        console.log('useCombinedStream: Combined stream created with',
          finalStream.getVideoTracks().length, 'video tracks and',
          finalStream.getAudioTracks().length, 'audio tracks');

        setCombinedStream(finalStream);
        setIsReady(true);
        setError(null);

      } catch (err) {
        console.error('useCombinedStream: Error creating combined stream:', err);
        setError(err.message);
        setIsReady(false);
      }
    };

    createCombinedStream();

    // Cleanup
    return () => {
      console.log('useCombinedStream: Cleaning up...');

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (localSourceRef.current) {
        localSourceRef.current.disconnect();
        localSourceRef.current = null;
      }
      if (remoteSourceRef.current) {
        remoteSourceRef.current.disconnect();
        remoteSourceRef.current = null;
      }

      // Stop tracks from video element srcObjects and clear them
      if (localVideoRef.current) {
        const srcObject = localVideoRef.current.srcObject;
        if (srcObject) {
          srcObject.getTracks().forEach(track => {
            track.stop();
            console.log('useCombinedStream: Stopped local video srcObject track:', track.kind);
          });
        }
        localVideoRef.current.srcObject = null;
      }
      if (remoteVideoRef.current) {
        const srcObject = remoteVideoRef.current.srcObject;
        if (srcObject) {
          srcObject.getTracks().forEach(track => {
            track.stop();
            console.log('useCombinedStream: Stopped remote video srcObject track:', track.kind);
          });
        }
        remoteVideoRef.current.srcObject = null;
      }

      // Stop combined stream tracks to release camera/mic
      setCombinedStream(prevStream => {
        if (prevStream) {
          prevStream.getTracks().forEach(track => {
            track.stop();
            console.log('useCombinedStream: Stopped combined track:', track.kind);
          });
        }
        return null;
      });
    };
  }, [localStream, remoteVideoTrack, remoteAudioTrack]);

  // Cleanup audio context and all resources on unmount
  useEffect(() => {
    return () => {
      console.log('useCombinedStream: Final cleanup on unmount...');

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

      // Stop all video element tracks
      if (localVideoRef.current?.srcObject) {
        localVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
        localVideoRef.current.srcObject = null;
      }
      if (remoteVideoRef.current?.srcObject) {
        remoteVideoRef.current.srcObject.getTracks().forEach(track => track.stop());
        remoteVideoRef.current.srcObject = null;
      }

      // Stop combined stream tracks
      setCombinedStream(prevStream => {
        if (prevStream) {
          prevStream.getTracks().forEach(track => {
            track.stop();
            console.log('useCombinedStream: Final cleanup stopped track:', track.kind);
          });
        }
        return null;
      });

      setIsReady(false);
    };
  }, []);

  return {
    combinedStream,
    isReady,
    error,
  };
};

export default useCombinedStream;
